import { useEffect, useRef, useState } from 'react';
import './searchForm.sass';

const MOBILE_QUERY = '(max-width: 850px)';
const isMobileViewport = () => window.matchMedia?.(MOBILE_QUERY).matches ?? false;

// A nivel de módulo porque en la primera búsqueda el formulario de la portada se desmonta y
// el que queda abajo es otra instancia: tiene que saber que la pregunta salió de un formulario.
let focusAfterSearch = false;

/** Elementos donde Enter ya tiene un uso propio (escribir, activar un botón o un enlace). */
function isInteractiveTarget(target) {
  return target instanceof Element
    && !!target.closest('input, textarea, select, button, a[href], [contenteditable="true"], [role="button"]');
}

/**
 * Formulario de búsqueda nativo de React.
 * Mantiene el texto como un borrador local para que, después de enviar una
 * consulta, el campo vuelva a quedar vacío como en la integración de WordPress.
 */
export default function SearchForm({ onSubmit, isSearching = false, isSearchActive = false }) {
  const [text, setText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const inputRef = useRef(null);
  const wasSearchingRef = useRef(isSearching);

  const containerRef = useRef(null);

  // El textarea se deshabilita mientras el agente responde y con eso pierde el foco: al
  // terminar se devuelve (sin mover la página) si la pregunta salió de este formulario.
  // En móvil no, para no abrir el teclado sin que la persona lo pida.
  useEffect(() => {
    const finished = wasSearchingRef.current && !isSearching;
    wasSearchingRef.current = isSearching;
    if (!finished || !focusAfterSearch) return;

    focusAfterSearch = false;
    if (!isMobileViewport()) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [isSearching]);

  // Enter fuera de un campo lleva al input (y lo trae a la vista si quedó abajo).
  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing || event.defaultPrevented) return;
      if (isSearching || isInteractiveTarget(event.target)) return;

      const input = inputRef.current;
      if (!input) return;

      event.preventDefault();
      input.focus({ preventScroll: true });

      const { top, bottom } = input.getBoundingClientRect();
      if (top < 0 || bottom > window.innerHeight) {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isSearching]);

  // Decide cuando el formulario deja de estar fijo abajo y pasa al flujo, justo
  // encima del footer. En lugar de un IntersectionObserver sobre el footer
  // (que oscila: al pasar el form de fixed->relative cambia el alto del
  // documento, mueve el footer y vuelve a disparar el observer -> "saltos"),
  // calculamos con getBoundingClientRect en scroll/resize.
  //
  // La clave para NO oscilar: comparamos el TOPE del footer contra el borde
  // inferior del viewport y aplicamos HISTERESIS (umbral distinto para activar
  // y desactivar) usando la altura real del formulario. Asi el punto en el que
  // se ancla y el punto en el que se suelta no coinciden y no hay rebote.
  useEffect(() => {
    if (!isSearchActive) {
      setIsFooterVisible(false);
      return undefined;
    }

    let cancelled = false;
    let frame = null;

    const evaluate = () => {
      frame = null;
      if (cancelled) return;

      const footer = document.querySelector('footer.footer, footer#footer, #footer');
      if (!footer) return;

      const footerTop = footer.getBoundingClientRect().top;
      const viewportH = window.innerHeight;
      const formH = containerRef.current?.offsetHeight || 0;

      // Margen de histeresis (px) para separar los umbrales de activar/soltar.
      const hysteresis = 24;

      setIsFooterVisible((prev) => {
        if (prev) {
          // Ya esta encima del footer: solo vuelve a fijo si el footer baja
          // claramente por debajo del punto donde cabria el form + margen.
          return footerTop <= viewportH - formH + hysteresis;
        }
        // Esta fijo abajo: ancla encima del footer cuando el tope del footer
        // sube por encima del borde inferior menos el alto del form.
        return footerTop <= viewportH - formH - hysteresis;
      });
    };

    const onScrollOrResize = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(evaluate);
    };

    // Evaluacion inicial (con reintento por si el footer aun no se inyecta).
    evaluate();
    const retryId = window.setInterval(() => {
      if (document.querySelector('footer.footer, footer#footer, #footer')) {
        evaluate();
        window.clearInterval(retryId);
      }
    }, 200);

    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });

    // La respuesta, el carrusel y las notas hacen crecer la página sin scroll ni resize: sin
    // esto el formulario se quedaba anclado sobre el footer, fuera de la vista. Se observa el
    // <main> y no el body: el alto del body cambia al pasar el form de fixed a relative y eso
    // volvía a disparar el cálculo en un ciclo.
    const content = document.querySelector('main.grid-layout');
    const contentObserver = typeof ResizeObserver === 'undefined' || !content
      ? null
      : new ResizeObserver(onScrollOrResize);
    contentObserver?.observe(content);

    return () => {
      cancelled = true;
      contentObserver?.disconnect();
      window.clearInterval(retryId);
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isSearchActive]);

  const handleClear = () => {
    setText('');
    inputRef.current?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const searchQuery = text.trim();
    if (!searchQuery || isSearching) {
      return;
    }

    focusAfterSearch = true;
    onSubmit(searchQuery);
    setText('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !isMobileViewport()) {
      handleSubmit(event);
      return;
    }

    if (event.key === 'Escape') {
      handleClear();
    }
  };

  return (
    <section
      ref={containerRef}
      className={`amx-search-fixed-container ${isSearchActive ? 'search-active' : ''} ${isFooterVisible ? 'footer-visible' : ''}`}
    >
      <div className="container-form">
        <form
          className={`amx-search-form ${isFocused ? 'amx-search_focused' : ''} ${isSearching ? 'searching' : ''}`}
          onSubmit={handleSubmit}
        >
          <div className="amx-search-input-container">
            <div className="dots_border" aria-hidden="true" />
            <textarea
              ref={inputRef}
              id="query"
              className="amx-search-input"
              name="q"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Haz una pregunta"
              autoComplete="off"
              autoCapitalize="off"
              enterKeyHint="enter"
              maxLength={8192}
              style={{ height: '24px' }}
              disabled={isSearching}
              aria-busy={isSearching}
            />

            <div className="amx-search-actions">
              {text && !isSearching && (
                <button
                  type="button"
                  className="buscador_search-btn search-clear-button"
                  onClick={handleClear}
                  aria-label="Limpiar texto"
                  title="Limpiar texto"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}

              <button
                type="submit"
                id="buscarBtn"
                className={`buscador_search-btn search-button search-btn-utv`}
                aria-label="Buscar"
                title="Buscar"
                disabled={isSearching || !text.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7-7 7 7" />
                </svg>
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};